import React, { useEffect, useMemo, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { Tooltip, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import InboxIcon from "@mui/icons-material/Inbox";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../examples/Footer";
import { appConfirm, appChoose } from "components/AppDialog";
import { RemoveRowButton } from "components/RowActions";

import { ListLink, CrmRowActions, ListIconButton, ListStatus, ListText, CrmPage, CrmPageHeader, CrmActionButton, CrmNotice, CrmTableCard, LoadTypeTabs } from "../crm/shared/CrmUi";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
const CARD_SX = {
  borderRadius: "10px",
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  boxShadow: "none",
};

const HEADER_CARD_SX = {
  borderRadius: "10px",
  background: "#f8fbff",
  border: "1px solid #e5e7eb",
  boxShadow: "none",
};

const chipSx = {
  fontWeight: 700,
  borderRadius: "999px",
  minWidth: 70,
  height: 28,
};

function ShipmentTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState({ ftl: [], ltl: [] });
  const [selectedRowsLTL, setSelectedRowsLTL] = useState([]);
  const [selectedRowsFTL, setSelectedRowsFTL] = useState([]);
  const [notification, setNotification] = useState({ value: false, text: "" });
  const [selectedPlannedRouteCountries, setSelectedPlannedRouteCountries] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const navigate = useNavigate();

  const MAX_LDM = 13.6;
  const MAX_CBM = 90;

  const selectedRowsLTLMemo = useMemo(() => {
    const map = new Map();
    (selectedRowsLTL || []).forEach((s) => map.set(String(s.id), s));
    return Array.from(map.values());
  }, [selectedRowsLTL]);

  const selectedRowsFTLMemo = useMemo(() => {
    const map = new Map();
    (selectedRowsFTL || []).forEach((s) => map.set(String(s.id), s));
    return Array.from(map.values());
  }, [selectedRowsFTL]);

  const selectedTotalsLTL = useMemo(() => {
    const totalLdm = selectedRowsLTLMemo.reduce(
        (sum, s) => sum + Number(s.attributes?.totals?.totalLdm || 0),
        0
    );
    const totalCbm = selectedRowsLTLMemo.reduce(
        (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || s.attributes?.totals?.totalVolume || 0),
        0
    );
    return { totalLdm, totalCbm };
  }, [selectedRowsLTLMemo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await CrudService.getShipments();
        setData(response?.data || []);
      } catch (error) {
        console.error("Error fetching shipments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const mergePlannedRouteCountries = (rows) => {
    const allCountries = rows.flatMap(
        (shipment) => shipment.attributes?.carbonFootprint?.plannedRouteCountries || []
    );
    return [...new Set(allCountries)];
  };

  const clickAddHandler = () => navigate("/shipping-order/create-shipment/details");

  const clickEditHandler = (id) =>
      navigate(`/shipping-order/create-shipment/details?id=${id}`);

  // A linked RHFL leaves its RMBL behind: if it was the RMBL's only RHFL the user decides
  // whether the RMBL goes too or stays for another RHFL; with other RHFLs it is only unlinked.
  const askRmblActionOnDelete = async (shipment) => {
    const linked = shipment?.attributes?.linkedRmbl;
    if (!linked) {
      return (await appConfirm("Are you sure you want to delete this shipment?")) ? "none" : null;
    }

    const rmblNo = linked.rmblNumber || "its RMBL";
    const otherCount = Math.max((linked.shipmentCount || 1) - 1, 0);

    if (otherCount > 0) {
      return (await appConfirm(
          `This RHFL is connected to ${rmblNo}, which has ${otherCount} other RHFL${otherCount > 1 ? "s" : ""}.
` +
              `The RHFL will be deleted and removed from ${rmblNo}; ${rmblNo} stays with its other RHFLs.`
      )) ? "keep" : null;
    }

    return appChoose({
      title: "Delete connected RHFL?",
      tone: "warning",
      message:
          `This RHFL is the only shipment on ${rmblNo}.

` +
          `Delete ${rmblNo} as well, or keep it so another RHFL can be connected to it?`,
      choices: [
        { label: `Keep ${rmblNo}`, value: "keep", tone: "info" },
        { label: `Delete RHFL + ${rmblNo}`, value: "delete", tone: "danger" },
      ],
    });
  };

  const clickDeleteHandler = async (_e, id) => {
    try {
      const shipment = data.find((row) => String(row.id) === String(id));
      const rmblAction = await askRmblActionOnDelete(shipment);
      if (!rmblAction) return;

      await CrudService.deleteShipment(id, rmblAction === "delete" ? "delete" : undefined);

      const updatedResponse = await CrudService.getShipments();
      setData(updatedResponse?.data || []);

      const rmblNo = shipment?.attributes?.linkedRmbl?.rmblNumber;
      const text =
          rmblAction === "delete"
              ? `Shipment and ${rmblNo} deleted successfully`
              : rmblAction === "keep"
                  ? `Shipment deleted; ${rmblNo} kept for another RHFL`
                  : "Shipment deleted successfully";
      setNotification({ value: true, text });
      setTimeout(() => setNotification({ value: false, text: "" }), 5000);
    } catch (error) {
      console.error("Error deleting shipment:", error);
      setNotification({ value: true, text: "Failed to delete shipment" });
      setTimeout(() => setNotification({ value: false, text: "" }), 5000);
    }
  };

  // An RHFL already on an RMBL (backend sends it as `linkedRmbl`) cannot be connected again.
  const linkedMasterMessage = (shipment) => {
    const linked = shipment?.attributes?.linkedRmbl;
    return linked ? `This shipment is already connected to ${linked.rmblNumber || "an RMBL"}.` : "";
  };

  const connectBlockedMessage = (rows) =>
      (rows || []).map(linkedMasterMessage).find(Boolean) || "";

  const notifyLinked = (shipment) => {
    const message = linkedMasterMessage(shipment);
    if (!message) return false;
    setNotification({ value: true, text: message });
    setTimeout(() => setNotification({ value: false, text: "" }), 5000);
    return true;
  };

  const handleAddRowLTL = (shipment) => {
    if (notifyLinked(shipment)) return;
    const shipmentTotals = shipment.attributes?.totals || {};

    const currentTotalLdm = selectedRowsLTL.reduce(
        (sum, s) => sum + Number(s.attributes?.totals?.totalLdm || 0),
        0
    );

    const currentTotalCbm = selectedRowsLTL.reduce(
        (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || s.attributes?.totals?.totalVolume || 0),
        0
    );

    const newLDM = currentTotalLdm + Number(shipmentTotals.totalLdm || 0);
    const newCBM = currentTotalCbm + Number(shipmentTotals.totalCbm || shipmentTotals.totalVolume || 0);

    if (newLDM > MAX_LDM || newCBM > MAX_CBM) {
      setNotification({
        value: true,
        text: "Cannot add this shipment. It exceeds the maximum limits.",
      });
      setTimeout(() => setNotification({ value: false, text: "" }), 5000);
      return;
    }

    if (selectedRowsLTL.some((r) => String(r.id) === String(shipment.id))) return;

    const next = [...selectedRowsLTL, shipment];
    setSelectedRowsLTL(next);
    setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
  };

  const handleRemoveRowLTL = (shipment) => {
    const next = selectedRowsLTL.filter((r) => String(r.id) !== String(shipment.id));
    setSelectedRowsLTL(next);
    setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
  };

  const handleAddRowFTL = (shipment) => {
    if (notifyLinked(shipment)) return;
    if (selectedRowsFTL.some((r) => String(r.id) === String(shipment.id))) return;
    setSelectedRowsFTL((prev) => [...prev, shipment]);
  };

  const handleRemoveRowFTL = (shipment) => {
    setSelectedRowsFTL((prev) => prev.filter((r) => String(r.id) !== String(shipment.id)));
  };

  const handleConnectMBLFromSelectedLTL = () => {
    navigate("/shipping-order/connect-mbl", {
      state: { selectedShipments: selectedRowsLTLMemo },
    });
  };

  const handleConnectMBLFromSelectedFTL = () => {
    navigate("/shipping-order/connect-mbl", {
      state: { selectedShipments: selectedRowsFTLMemo },
    });
  };

  const formatNumber = (value) => (value ? Number(value).toFixed(4) : "0.0000");

  const getSellingRate = (shipment) => {
    const finalRate =
        shipment.attributes?.finalSellingRate ??
        shipment.attributes?.sellingRate ??
        shipment.attributes?.sallingRate ??
        0;

    return `${Number(finalRate || 0).toFixed(2)} ${
        shipment.attributes?.sellingRateCurrency || shipment.attributes?.currency || ""
    }`;
  };

  const renderShipmentNoCell = (id, label) => (
      <ListLink onClick={() => clickEditHandler(id)}>
        {label}
      </ListLink>
  );

  const renderTransportTypeBadge = (type) => (
      <ListStatus label={type || "N/A"} tone={type.includes("FTL") ? "green" : "purple"} />
  );

  const renderLdmChip = (value) => (
      <ListText primary={`${formatNumber(value)} m`} strong />
  );

  const renderMoneyChip = (value) => (
      <ListText primary={value} strong />
  );

  const renderActionsCell = (shipment, { isFTL, isLTL, alreadySelectedFTL, alreadySelectedLTL }) => (
      <CrmRowActions onEdit={() => clickEditHandler(shipment.id)} editTitle="Edit Shipment" onDelete={(e) => clickDeleteHandler(e, shipment.id)} deleteTitle="Delete Shipment">{isFTL && <ListIconButton title={linkedMasterMessage(shipment) || (alreadySelectedFTL ? "Already Selected" : "Add Shipment")} onClick={() => handleAddRowFTL(shipment)} disabled={alreadySelectedFTL || Boolean(linkedMasterMessage(shipment))} tone="green"><AddIcon /></ListIconButton>}{isLTL && <ListIconButton title={linkedMasterMessage(shipment) || (alreadySelectedLTL ? "Already Selected" : "Add Shipment")} onClick={() => handleAddRowLTL(shipment)} disabled={alreadySelectedLTL || Boolean(linkedMasterMessage(shipment))} tone="green"><AddIcon /></ListIconButton>}</CrmRowActions>
  );

  const getRows = (dataList) => {
    const ftlRows = [];
    const ltlRows = [];

    (dataList || []).forEach((shipment) => {
      const shipmentTotals = shipment.attributes?.totals || {};
      const plannedRouteCountries =
          shipment.attributes?.carbonFootprint?.plannedRouteCountries || ["No countries found"];

      const transportType = shipment.attributes?.roadTransportType || "";
      const isLTL = transportType.includes("LTL");
      const isFTL = transportType.includes("FTL");

      const alreadySelectedLTL = selectedRowsLTL.some(
          (r) => String(r.id) === String(shipment.id)
      );

      const alreadySelectedFTL = selectedRowsFTL.some(
          (r) => String(r.id) === String(shipment.id)
      );

      const shipmentNoLabel = `RHFL${shipment.attributes?.shipmentNo || 0}`;

      const rowData = {
        id: shipment.id,
        shipmentNo: shipmentNoLabel,
        shipmentNoCell: renderShipmentNoCell(shipment.id, shipmentNoLabel),
        plateContainerNumber: shipment.attributes?.plateContainerNumber,
        customer: shipment.attributes?.customer
            ? shipment.attributes.customer.length > 38
                ? `${shipment.attributes.customer.substring(0, 36)}...`
                : shipment.attributes.customer
            : "No Customer Data",
        roadTransportType: transportType,
        roadTransportTypeCell: renderTransportTypeBadge(transportType),
        totalLdm: `${formatNumber(shipmentTotals.totalLdm)} m`,
        totalLdmCell: renderLdmChip(shipmentTotals.totalLdm),
        totalVolume: formatNumber(shipmentTotals.totalCbm || shipmentTotals.totalVolume),
        totalChargeableWeight: shipmentTotals.totalChargeableWeight || 0,
        plannedRouteCountries: plannedRouteCountries.join(", "),
        sellingRate: getSellingRate(shipment),
        sellingRateCell: renderMoneyChip(getSellingRate(shipment)),
        actions: renderActionsCell(shipment, { isFTL, isLTL, alreadySelectedFTL, alreadySelectedLTL }),
      };

      if (isFTL) ftlRows.push(rowData);
      if (isLTL) ltlRows.push(rowData);
    });

    return { ftl: ftlRows, ltl: ltlRows };
  };

  useEffect(() => {
    setTableData(getRows(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedRowsLTL, selectedRowsFTL]);

  const headerCellStyle = { fontWeight: "bold", fontSize: "14px" };

  const selectedTableColumns = [
    {
      Header: () => <span style={headerCellStyle}>Shipment No</span>,
      accessor: "shipmentNo",
      Cell: ({ row }) => row.original.shipmentNoCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Plate No</span>,
      accessor: "plateContainerNumber",
    },
    {
      Header: () => <span style={headerCellStyle}>Customer</span>,
      accessor: "customer",
    },
    {
      Header: () => <span style={headerCellStyle}>Road Transport Type</span>,
      accessor: "roadTransportType",
      Cell: ({ row }) => row.original.roadTransportTypeCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Selling Rate</span>,
      accessor: "sellingRateCurrency",
      Cell: ({ row }) => row.original.sellingRateCurrencyCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Actions</span>,
      accessor: "actions",
      disableSortBy: true,
    },
  ];

  const getSelectedRowsTable = (selectedData, type) =>
      (selectedData || []).map((shipment) => {
        const shipmentTotals = shipment.attributes?.totals || {};
        const shipmentNoLabel = `RHFL${shipment.attributes?.shipmentNo || 0}`;

        return {
          id: shipment.id,
          shipmentNo: shipmentNoLabel,
          shipmentNoCell: renderShipmentNoCell(shipment.id, shipmentNoLabel),
          plateContainerNumber: shipment.attributes?.plateContainerNumber,
          customer: shipment.attributes?.customer
              ? shipment.attributes.customer.length > 38
                  ? `${shipment.attributes.customer.substring(0, 36)}...`
                  : shipment.attributes.customer
              : "Unknown Customer",
          roadTransportType: shipment.attributes?.roadTransportType || "",
          roadTransportTypeCell: renderTransportTypeBadge(shipment.attributes?.roadTransportType || ""),
          totalLdm: `${formatNumber(shipmentTotals.totalLdm)} m`,
          totalLdmCell: renderLdmChip(shipmentTotals.totalLdm),
          totalVolume: formatNumber(shipmentTotals.totalCbm || shipmentTotals.totalVolume),
          totalChargeableWeight: shipmentTotals.totalChargeableWeight || 0,
          sellingRateCurrency: getSellingRate(shipment),
          sellingRateCurrencyCell: renderMoneyChip(getSellingRate(shipment)),
          actions: (
              <MDBox display="flex" alignItems="center">
                <Tooltip title="Remove Shipment">
                  <RemoveRowButton onClick={() =>
                          type === "FTL"
                              ? handleRemoveRowFTL(shipment)
                              : handleRemoveRowLTL(shipment)
                      } tooltip="Remove row ftl" />
                </Tooltip>
              </MDBox>
          ),
        };
      });

  const selectedDataTableDataFTL = {
    columns: selectedTableColumns,
    rows: getSelectedRowsTable(selectedRowsFTLMemo, "FTL"),
  };

  const selectedDataTableDataLTL = {
    columns: selectedTableColumns,
    rows: getSelectedRowsTable(selectedRowsLTLMemo, "LTL"),
  };

  const dataTableColumns = [
    {
      Header: () => <span style={headerCellStyle}>Shipment No</span>,
      accessor: "shipmentNo",
      Cell: ({ row }) => row.original.shipmentNoCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Plate No</span>,
      accessor: "plateContainerNumber",
    },
    {
      Header: () => <span style={headerCellStyle}>Customer</span>,
      accessor: "customer",
    },
    {
      Header: () => <span style={headerCellStyle}>Road Transport Type</span>,
      accessor: "roadTransportType",
      Cell: ({ row }) => row.original.roadTransportTypeCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Selling Rate</span>,
      accessor: "sellingRate",
      Cell: ({ row }) => row.original.sellingRateCell,
    },
    {
      Header: () => <span style={headerCellStyle}>Actions</span>,
      accessor: "actions",
      disableSortBy: true,
    },
  ];

  const handleTabChange = (_event, newValue) => setTabValue(newValue);

  const EmptyState = ({ label }) => (
      <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          gap={1}
      >
        <InboxIcon sx={{ fontSize: 42, color: "text.disabled" }} />
        <MDTypography variant="button" color="secondary" fontWeight="regular">
          {label}
        </MDTypography>
      </MDBox>
  );

  const CapacityGauge = ({ label, value, max, unit, color }) => {
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
        <MDBox flex={1} minWidth={180}>
          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {label}
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" color={color}>
              {value.toFixed(2)} / {max} {unit}
            </MDTypography>
          </MDBox>
          <MDProgress
              variant="gradient"
              color={pct >= 100 ? "error" : pct >= 80 ? "warning" : color}
              value={pct}
          />
        </MDBox>
    );
  };

  return (
      <DashboardLayout>
        <DashboardNavbar />
        <CrmPage>
          <CrmPageHeader
              title="Road Shipments"
              subtitle={`${tableData.ftl.length} FTL · ${tableData.ltl.length} LTL shipments. Open a shipment number to edit it, or add shipments to an RMBL.`}
              actions={
                <CrmActionButton icon={<AddIcon />} onClick={clickAddHandler}>
                  New Shipment
                </CrmActionButton>
              }
          />

          {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

          <LoadTypeTabs

              options={[

                  { value: 0, label: "FTL", caption: "Full truck load", count: tableData.ftl.length, icon: <LocalShippingOutlined /> },

                  { value: 1, label: "LTL", caption: "Part load / groupage", count: tableData.ltl.length, icon: <Inventory2Outlined /> },

              ]}

              value={tabValue}

              onChange={(value) => setTabValue(value)}

          />

          <CrmTableCard
              title={tabValue === 0 ? "FTL shipments" : "LTL shipments"}
              summary={`${tabValue === 0 ? tableData.ftl.length : tableData.ltl.length} shipments`}
          >
            {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                  <CircularProgress size={32} />
                </MDBox>
            ) : (
                <>
                  {tabValue === 0 && (
                      tableData.ftl.length > 0 ? (
                          <DataTable table={{ columns: dataTableColumns, rows: tableData.ftl }} canSearch />
                      ) : (
                          <EmptyState label="No FTL shipments yet." />
                      )
                  )}
                  {tabValue === 1 && (
                      tableData.ltl.length > 0 ? (
                          <DataTable table={{ columns: dataTableColumns, rows: tableData.ltl }} canSearch />
                      ) : (
                          <EmptyState label="No LTL shipments yet." />
                      )
                  )}
                </>
            )}
          </CrmTableCard>

          {tabValue === 0 && selectedRowsFTLMemo.length > 0 && (
              <CrmTableCard title="Selected shipments (FTL)" summary={`${selectedRowsFTLMemo.length} selected · ready to connect to an RMBL`}>
                <DataTable table={selectedDataTableDataFTL} canSearch />
                <MDBox display="flex" justifyContent="flex-end" p={2.5} pt={0}>
                  <CrmActionButton
                      icon={<LinkIcon />}
                      onClick={handleConnectMBLFromSelectedFTL}
                      disabled={Boolean(connectBlockedMessage(selectedRowsFTLMemo))}
                      title={connectBlockedMessage(selectedRowsFTLMemo) || undefined}
                  >
                    Connect RMBL
                  </CrmActionButton>
                </MDBox>
              </CrmTableCard>
          )}

          {tabValue === 1 && selectedRowsLTLMemo.length > 0 && (
              <CrmTableCard title="Selected shipments (LTL)" summary={`${selectedRowsLTLMemo.length} selected · check the capacity before connecting`}>
                <MDBox display="flex" flexWrap="wrap" gap={3} px={2.5} pt={2}>
                  <CapacityGauge
                      label="Total LDM"
                      value={selectedTotalsLTL.totalLdm}
                      max={MAX_LDM}
                      unit="m"
                      color="info"
                  />
                  <CapacityGauge
                      label="Total CBM"
                      value={selectedTotalsLTL.totalCbm}
                      max={MAX_CBM}
                      unit="m³"
                      color="success"
                  />
                </MDBox>
                <DataTable table={selectedDataTableDataLTL} canSearch />
                <MDBox display="flex" justifyContent="flex-end" p={2.5} pt={0}>
                  <CrmActionButton
                      icon={<LinkIcon />}
                      onClick={handleConnectMBLFromSelectedLTL}
                      disabled={Boolean(connectBlockedMessage(selectedRowsLTLMemo))}
                      title={connectBlockedMessage(selectedRowsLTLMemo) || undefined}
                  >
                    Connect RMBL
                  </CrmActionButton>
                </MDBox>
              </CrmTableCard>
          )}
        </CrmPage>

        <Footer />
      </DashboardLayout>
  );
}

export default ShipmentTable;
